
import React, { Component } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { Container, Row, Col } from 'react-grid-system'
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';

import {
  UniTable,
  UniIcon,
  UniConditionalRender,
  UniLocalize,
  UniInput,
  UniKeyVal,
  Editable,
  IUniTable_Column,
  IPaginatedResponseC,
  IPaginationQueryBuilderParamsC,
  ICredentialC,
  CredentialC,
  DealerC,
  OrganizationC,
  IUniTable_UpdatePaginationSummary,
  IUniTable_Filter,
  IUniTable_Sort,
  IUniTable_PaginationSummary,
  IUniConfirm_Config,
  IPartnerCredImpls,
  WiegandCredentialC,
  UniKeyV1CredentialC,
  RawCredentialC,
  ECredentialStatus,
  ECredentialType,
  EOperationCodesC,
  ES10nSettings,
  emailV10n,
  UniTag,
  IUniTable_Props,
  EUniTable_FooterStyle,
  IRouteMatchParamsHOCProps,
  withRouteMatchParams,
  IUniChips_Chip
} from '@unikey/unikey-commons/release/comm';

import {
  toggleCreateCredentialModal,
  attemptRetrieveOrgDetails,
  attemptRetrieveDealerDetails,
  updateCredentialListQueryParams,
  attemptRetrieveCredentialsList,
  updateCredentialsTableMeta,
  openConfirmModal,
  closeConfirmModal,
  attemptRevokeCredential,
  attemptDisableCredential,
  attemptEnableCredential,
  attemptReissueCredential,
  attemptCopyCredential,
  attemptDeleteCredential,
  attemptToggleCredentialPermissions,
  handleCopyCredToEmailChange,

  navConfig, ENavPages,
  s10n, canI,
  getTableSortDirection, getTableParamsFromUpdate,
  buildTableUpdateFunc, TTableUpdateFunc,
  PartnerCustomizations, IPartnerCustomizations,
  IGetPartnerCredsActionParams,
  ICopyCredentialActionParams,
  IPendingRedirectActionParams,
  addPendingRedirect
} from '../internal';

interface IProps extends WrappedComponentProps, IPartnerCustomizations, IRouteMatchParamsHOCProps {
  credentialData: IPaginatedResponseC<ICredentialC, CredentialC>,
  credentialDataModels: CredentialC[],
  credentialListQueryParams: IPaginationQueryBuilderParamsC,
  appliedFilters: IUniTable_Filter[],
  appliedSorts: IUniTable_Sort[],
  paginationSummary: IUniTable_PaginationSummary,
  listLoading: boolean,
  orgDetails: OrganizationC,
  orgLoading: boolean,
  dealerData: DealerC,
  copyCredToEmail: Editable<string>,

  isMini: boolean,
  orgId: string,

  partnerDefaultCredentialType: ECredentialType,
  partnerCredImpls: IPartnerCredImpls,

  preventToggleUserInstaller?: boolean,
  subAllowEnabledCredReissue: boolean,

  updateCopyCredentialTo(email: Editable<string>): void,
  toggleCredentialPermissions(existingCredential: CredentialC): Promise<void>,
  copyCredential(copyActionParams: ICopyCredentialActionParams): Promise<CredentialC>,
  revokeCredential(credId: string): Promise<void>,
  reissueCredential(credId: string): Promise<void>,
  enableCredential(credId: string): Promise<void>,
  disableCredential(credId: string): Promise<void>,
  deleteCredential(credId: string): Promise<void>,
  updateQueryParams?(params: IPaginationQueryBuilderParamsC): void,
  updateTableMeta(metaSummary: IUniTable_UpdatePaginationSummary): void,
  getAccountDetails(): void,
  toggleCreateModal(): void,
  getOrgDetails(orgId: string): Promise<void>,
  getOrgCredentials(): Promise<void>,
  closeConfirmModal(): void,
  openConfirmDialog(dialogConfig: IUniConfirm_Config): void,
  addPendingRedirect(redirOpts: IPendingRedirectActionParams): void,
}

class CredentialListContainer extends Component<IProps> {
  _updateTable: TTableUpdateFunc; 

  constructor(props: IProps) {
    super(props);

    this._updateTable = buildTableUpdateFunc(
      props.getOrgCredentials,
      props.updateTableMeta,
      props.updateQueryParams
    )
  }

  componentDidMount() {
    if (Object.keys(this.props.dealerData).length === 0) {
      this.props.getAccountDetails();
    }
    this.props.getOrgDetails(this.props.orgId);
    this.props.getOrgCredentials();
  }


  _revokeCredentialAndRefreshList = (credId: string) => {
    return this.props.revokeCredential(credId).then(() => {
      this.props.getOrgCredentials();
    })
  }

  _copyCredentialAndRefreshList = (credId: string, emailTo?: string) => {
    return this.props.copyCredential({ credId, email: emailTo }).then(() => {
      this.props.getOrgCredentials();
    })
  }

  _enableCredentialAndRefreshList = (credId: string) => {
    return this.props.enableCredential(credId).then(() => {
      this.props.getOrgCredentials();
    })
  }

  _disableCredentialAndRefreshList = (credId: string) => {
    return this.props.disableCredential(credId).then(() => {
      this.props.getOrgCredentials();
    })
  }

  _deleteCredentialAndRefreshList = (credId: string) => {
    return this.props.deleteCredential(credId).then(() => {
      this.props.getOrgCredentials();
    })
  }

  _toggleCredentialPermissionsAndRefreshList = (cred: CredentialC) => {
    return this.props.toggleCredentialPermissions(cred).then(() => {
      this.props.getOrgCredentials();
    })
  }

  _buildColumnsAndActions() {

    // permissions
    const permissionToReissueCredential = canI(EOperationCodesC.CreateCredential, this.props.activeDealerId, this.props.orgId); // yes its a mismatched name - operation
    const permissionToUpdateCredential = canI(EOperationCodesC.UpdateCredential, this.props.activeDealerId, this.props.orgId);
    const permissionToRevokeCredential = canI(EOperationCodesC.DeleteCredential, this.props.activeDealerId, this.props.orgId); // yes its a mismatched name - operation
    const permissionToDeleteCredential = canI(EOperationCodesC.DeleteCredential, this.props.activeDealerId, this.props.orgId);
    const permissionToDisableCredential = canI(EOperationCodesC.DisableCredential, this.props.activeDealerId, this.props.orgId);

    const columns = new Map<string, IUniTable_Column>()
      .set('email', {
        nameKey: 'email',
        isSortable: true,
        sortFields: ['email'],
        isPrimaryFilter: true,
        size: 6,
      })
      .set('facilityCode', {
        nameKey: 'facility',
        isSortable: false,
        size: 2,
        template: (rowItem: CredentialC) => rowItem.facilityCode ? Number(rowItem.facilityCode) : (<i><UniLocalize translate='_notApplicable' /></i>)
      })
      .set('cardNumber', {
        nameKey: 'cardNumber',
        isSortable: false,
        size: 4,
        template: (rowItem: CredentialC) => rowItem.cardNumber ? rowItem.cardNumber : (<i><UniLocalize translate='_notApplicable' /></i>)
      })
      .set('type', {
        nameKey: 'format',
        isSortable: false,
        type: 'enum-taginverted',
        enumType: ECredentialType,
        size: 4,
      })
      .set('status', {
        nameKey: 'status',
        isSortable: true,
        sortFields: ['status'],
        type: 'enum-tagdot',
        enumType: ECredentialStatus,
        size: 4,
      })
      .set('installerIndicator', {
        nameKey: '_emptyString',
        isSortable: false,
        size: 2,
        template: (rowItem: CredentialC) => rowItem.label === 'installer' ? (<i><UniIcon size="sm" name="build" tooltipTextKeys={['installer']} /></i>) : <></>
      })
      .set('actions', {
        nameKey: 'actions',
        isSortable: false,
        size: 2,
        collapsed: true
      });

    const actions = new Map()
      .set('view', {
        nameKey: 'view',
        icon: 'removeRedEye',
        isDefaultAction: true,
        func: (rowItem: CredentialC) => this.props.addPendingRedirect({ 
          pathname: navConfig.get(ENavPages.credentialDetails)!.linkTo([this.props.orgId, rowItem.id])!
        }),
      })
      .set('copy', {
        nameKey: 'copy',
        icon: 'contentCopy',
        func: (rowItem: CredentialC) => this.props.openConfirmDialog({
          titleKey: 'copy',
          titleIcon: 'contentCopy',
          messageKeys: ['youAreAboutToCopyThisCredential', 'areYouSure'],
          confirmTextKey: 'copy',
          cancelHandler: this.props.closeConfirmModal,
          customContentRender: () => (
            <UniInput
              editable={new Editable({ value: rowItem.email })}
              type="string"
              placeholderKey={rowItem.email}
              labelKey="copyCredentialTo"
              handleUpdate={(email: Editable<string>) => this.props.updateCopyCredentialTo(email)}
              validateOnInitialRender={true}
              validations={[emailV10n]} />
          ),
          confirmDisabled: () => false, // TODO: !this.props.copyCredToEmail.valid,
          confirmHandler: () => {
            this._copyCredentialAndRefreshList(rowItem.id, this.props.copyCredToEmail.value),
              this.props.closeConfirmModal()
          }
        }),
        evalVisible: (rowItem: CredentialC) => !!permissionToReissueCredential && rowItem.type !== ECredentialType.string,
      })
      .set('reissue', {
        nameKey: 'reissue',
        icon: 'removeRedEye',
        func: (rowItem: CredentialC) => this.props.openConfirmDialog({
          titleKey: 'reissue',
          titleIcon: 'send',
          messageKeys: ['reissueCredentialMessage', 'areYouSure'],
          confirmTextKey: 'reissue',
          cancelHandler: this.props.closeConfirmModal,
          confirmHandler: () => {
            this.props.reissueCredential(rowItem.id),
              this.props.closeConfirmModal()
          }
        }),
        evalVisible: (rowItem: CredentialC) => !(!permissionToReissueCredential || this.props.subAllowEnabledCredReissue ? false : rowItem.status > ECredentialStatus.invited),
        evalDisabled: (rowItem: CredentialC) => this.props.subAllowEnabledCredReissue ? false : rowItem.status !== ECredentialStatus.invited,
      })
      .set('disable', {
        nameKey: 'disable',
        icon: 'removeRedEye',
        func: (rowItem: CredentialC) => this._disableCredentialAndRefreshList(rowItem.id),
        // can only mark as disabled, if the credential is currently enabled
        evalDisabled: (rowItem: CredentialC) => rowItem.status !== ECredentialStatus.enabled,
        evalVisible: (rowItem: CredentialC) => permissionToDisableCredential && rowItem.status === ECredentialStatus.enabled
      })
      .set('enable', {
        nameKey: 'enable',
        icon: 'removeRedEye',
        func: (rowItem: CredentialC) => this._enableCredentialAndRefreshList(rowItem.id),
        // can only mark as enabled, if the credential is currently disabled
        evalVisible: (rowItem: CredentialC) => rowItem.status === ECredentialStatus.disabled
      })
      .set('revoke', {
        nameKey: 'revoke',
        icon: 'removeRedEye',
        theme: 'error',
        evalVisible: (rowItem: CredentialC) => permissionToRevokeCredential,
        func: (rowItem: CredentialC) => this.props.openConfirmDialog({
          titleKey: 'revokeCredential',
          titleIcon: 'delete',
          messageKeys: ['youAreAboutToRevokeThisCredential', 'areYouSure'],
          confirmTextKey: 'revoke',
          cancelHandler: this.props.closeConfirmModal,
          confirmHandler: () => {
            this._revokeCredentialAndRefreshList(rowItem.id),
              this.props.closeConfirmModal()
          }
        })
      })
      .set('delete', {
        nameKey: 'delete',
        icon: 'delete',
        theme: 'error',
        evalVisible: (rowItem: CredentialC) => permissionToDeleteCredential && rowItem.status === ECredentialStatus.revoked,
        func: (rowItem: CredentialC) => this.props.openConfirmDialog({
          titleKey: 'deleteCredential',
          titleIcon: 'delete',
          messageKeys: ['youAreAboutToPermanentlyDeleteThisCredential', 'areYouSure'],
          confirmTextKey: 'delete',
          cancelHandler: this.props.closeConfirmModal,
          confirmHandler: () => {
            this._deleteCredentialAndRefreshList(rowItem.id),
              this.props.closeConfirmModal()
          }
        })
      })
      .set('addInstallerPermissions', {
        nameKey: 'addInstallerPermissions',
        icon: '',
        func: (rowItem: CredentialC) => this._toggleCredentialPermissionsAndRefreshList(rowItem),
        evalVisible: (rowItem: CredentialC) => permissionToUpdateCredential && !rowItem.isInstaller && !this.props.preventToggleUserInstaller && this.props.orgDetails.credIssueConfig?.mobileApp?.isAllowed
      })
      .set('removeInstallerPermissions', {
        nameKey: 'removeInstallerPermissions',
        icon: '',
        func: (rowItem: CredentialC) => this._toggleCredentialPermissionsAndRefreshList(rowItem),
        evalVisible: (rowItem: CredentialC) => permissionToUpdateCredential && rowItem.isInstaller && !this.props.preventToggleUserInstaller && this.props.orgDetails.credIssueConfig?.mobileApp?.isAllowed,
      })

    return { columns, actions };
  }

  _buildMiniColumns() {
    const columns = new Map<string, IUniTable_Column>()
      .set('email', {
        nameKey: 'email',
        isSortable: true,
        sortFields: ['email'],
        isPrimaryFilter: true,
        size: 10,
      })
      .set('type', {
        nameKey: 'format',
        isSortable: false,
        type: 'enum-taginverted',
        enumType: ECredentialType,
        size: 8,
      })
      .set('status', {
        nameKey: 'status',
        isSortable: true,
        sortFields: ['status'],
        type: 'enum-tagdot',
        enumType: ECredentialStatus,
        size: 6,
      })

    const actions = new Map()
      .set('view', {
        nameKey: 'view',
        icon: 'removeRedEye',
        isDefaultAction: true,
        func: (rowItem: CredentialC) => this.props.addPendingRedirect({ 
          pathname: navConfig.get(ENavPages.credentialDetails)!.linkTo([this.props.orgId, rowItem.id])!
        }),
      });

    return { miniColumns: columns, miniActions: actions };
  }

  render() {
    if (this.props.render) {
      return this.props.render();
    }

    // permissions
    const permissionToCreateCredential = canI(EOperationCodesC.CreateCredential, this.props.activeDealerId, this.props.orgId);

    const { columns, actions } = this._buildColumnsAndActions();

    // non mini props 
    var sizeDependentTableProps: Partial<IUniTable_Props> = {
      preTitleDisplay: (
        <UniTag textKey={this.props.orgDetails.name ?? ''} icon="business" inverted allowWrap useDefaultColor withNeighborTag />
      )
    };

    // access methods settings
    const defaultAccessMethods = new Map<string, IUniChips_Chip>();
    const allowedAccessMethods = new Map<string, IUniChips_Chip>();

    if (this.props.orgDetails.credIssueConfig?.mobileApp?.isAllowed) {
      allowedAccessMethods.set('bluetooth', { disabled: false, nameKey: 'bluetooth' });
    }
    if (this.props.orgDetails.credIssueConfig?.mobileApp?.enabledByDefault) {
      defaultAccessMethods.set('bluetooth', { disabled: false, nameKey: 'bluetooth' });
    }

    if (this.props.orgDetails.credIssueConfig?.appleWallet?.isAllowed) {
      allowedAccessMethods.set('appleWallet', { disabled: true, nameKey: 'appleWallet' });
    }
    if (this.props.orgDetails.credIssueConfig?.appleWallet?.enabledByDefault) {
      defaultAccessMethods.set('appleWallet', { disabled: true, nameKey: 'appleWallet' });      
    }

    // boolean to track if more than one configurable access method is allowed (google/apple wallet, bluetooth) 
    // if not, dont show the access method toggles at all -- only show when configurable
    const isAppleWalletAccessAllowedAndConfigurable = this.props.orgDetails.credIssueConfig?.appleWallet?.isAllowed && this.props.orgDetails.credIssueConfig?.appleWallet?.overrideable;
    const isGoogleWalletAccessAllowedAndConfigurable = this.props.orgDetails.credIssueConfig?.googleWallet?.isAllowed && this.props.orgDetails.credIssueConfig?.googleWallet?.overrideable;
    const isMobileAccessAllowedAndConfigurable = this.props.orgDetails.credIssueConfig?.mobileApp?.isAllowed && this.props.orgDetails.credIssueConfig?.mobileApp?.overrideable;
    const multipleConfigurableCredentialAccessMethods = [
      isAppleWalletAccessAllowedAndConfigurable,
      isGoogleWalletAccessAllowedAndConfigurable,
      isMobileAccessAllowedAndConfigurable
    ].filter(accessMethodConfigurable => !!accessMethodConfigurable).length > 1

    // overwrite dynamic props if mini desired
    if (this.props.isMini) {
      const { miniColumns, miniActions } = this._buildMiniColumns();
      sizeDependentTableProps = {
        searchable: false,
        createButtonTextHidden: true,
        handleCreateClick: permissionToCreateCredential ? this.props.toggleCreateModal : undefined,
        preTitleDisplay: undefined,
        data: this.props.credentialDataModels?.slice(0, 5),
        columnConfig: miniColumns,
        actionsConfig: miniActions,
        preventDynamicColumnHiding: true,
        loadMoreTextKey: 'seeAllOrgCredentials',
        loadMoreAction: () => this.props.addPendingRedirect({ pathname: navConfig.get(ENavPages.credentials)!.linkTo([this.props.orgId ?? '']) }),
        footerStyle: EUniTable_FooterStyle.loadMoreWithSummary,
      };
    }

    return (
      <>
        <section className='credentialList-container'>
          <UniConditionalRender visible={!this.props.isMini}>
            <Row>
              <UniConditionalRender visible={multipleConfigurableCredentialAccessMethods}>
                <Col sm={14}>
                  <UniKeyVal
                    key="org-wallet-settings"
                    label="org-wallet-settings"
                    stacked={true}
                    showLoader={!this.props.orgDetails.name || this.props.orgLoading}
                    preventEdit={true}
                    fields={[
                      {
                        keyName: 'allowedAccessMethods',
                        value: this.props.orgDetails.credIssueConfig?.appleWallet?.isAllowed,
                        type: 'chips',
                        collection: allowedAccessMethods,
                        keyInfo: {
                          icon: 'infoOutline',
                          textKeys: [
                            'allowedAccessMethods',
                            '_explainAllowedAccessMethodsOrgSetting'
                          ]
                        }
                      },
                      {
                        keyName: 'defaultAccessMethods',
                        value: this.props.orgDetails.credIssueConfig?.appleWallet?.enabledByDefault,
                        type: 'chips',
                        collection: defaultAccessMethods,
                        keyInfo: {
                          icon: 'infoOutline',
                          textKeys: [
                            'defaultAccessMethods',
                            '_explainDefaultAccessMethodsOrgSetting'
                          ]
                        }
                      }
                    ]} />
                </Col>
              </UniConditionalRender>
            </Row>
          </UniConditionalRender> 

          <UniTable
            searchable={true}
            titleKey="credentialsList"
            createButtonTextKey="credential"
            handleUpdate={this._updateTable}
            handleCreateClick={permissionToCreateCredential ? this.props.toggleCreateModal : undefined}
            data={this.props.credentialDataModels} 
            columnConfig={columns}
            actionsConfig={actions}
            activeSorts={this.props.appliedSorts}
            activeFilters={this.props.appliedFilters}
            paginationSummary={this.props.paginationSummary}
            showLoader={this.props.listLoading} 
            {...sizeDependentTableProps} />
        </section>
      </>
    )
  }
}

function mapStateToProps(state: any, ownProps: IProps) {
  const orgId = ownProps.orgId ?? ownProps.match?.params?.organizationId;
  return {
    orgId,
    dealerData: state.dealer.dealerData,
    credentialData: state.credentials.data,
    credentialDataModels: state.credentials.data.models ?? [],
    credentialListQueryParams: state.credentials.queryParams,
    appliedFilters: state.credentials.tableFilters,
    appliedSorts: state.credentials.tableSorts,
    paginationSummary: state.credentials.paginationSummary,
    listLoading: state.credentials.loading,
    orgDetails: state.orgDetails.origOrg,
    orgLoading: state.orgDetails.loading,
    copyCredToEmail: state.credentialDetails.copyToEmail,

    // subscription
    subFreeInstallerCredentials: s10n(ES10nSettings.FreeInstallerCredentials),
  }
}

const mapDispatchToProps = (dispatch: any, ownProps: IProps) => {
  const orgId = ownProps.orgId ?? ownProps.match?.params?.organizationId;
  const partnerCredActionParams: IGetPartnerCredsActionParams = {
    partnerCredImpls: ownProps.partnerCredImpls,
    orgId
  };

  return bindActionCreators({
    toggleCreateModal: toggleCreateCredentialModal,
    getOrgDetails: attemptRetrieveOrgDetails,
    getAccountDetails: attemptRetrieveDealerDetails,
    closeConfirmModal,
    openConfirmDialog: openConfirmModal,
    openDeleteCredentialConfirmDialog: openConfirmModal,
    updateCopyCredentialTo: handleCopyCredToEmailChange,

    deleteCredential: attemptDeleteCredential,
    reissueCredential: attemptReissueCredential,
    copyCredential: attemptCopyCredential,
    revokeCredential: attemptRevokeCredential,
    enableCredential: attemptEnableCredential,
    disableCredential: attemptDisableCredential,
    toggleCredentialPermissions: attemptToggleCredentialPermissions,
    
    getOrgCredentials: attemptRetrieveCredentialsList.bind(null, partnerCredActionParams),
    updateQueryParams: updateCredentialListQueryParams,
    updateTableMeta: updateCredentialsTableMeta,
    
    addPendingRedirect
  }, dispatch)
}

export default PartnerCustomizations(withRouteMatchParams(
  connect(mapStateToProps, mapDispatchToProps)(
    injectIntl(CredentialListContainer)
  )), { componentName: 'CredentialList' })